import React, { useState } from 'react';
import {
  App,
  Button,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Space,
  Switch,
  Table,
  Tag,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';

const GradesPage: React.FC = () => {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form] = Form.useForm();

  const { data: rows = [], isLoading } = useQuery({
    queryKey: hrmsKeys.grades(),
    queryFn: () => hrmsService.listGrades(),
  });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ level_rank: 1, is_active: true });
    setDrawerOpen(true);
  };

  const openEdit = (r: any) => {
    setEditing(r);
    form.setFieldsValue({
      grade_code: r.grade_code,
      grade_name: r.grade_name,
      level_rank: r.level_rank ?? 1,
      description: r.description,
      is_active: r.is_active !== false,
    });
    setDrawerOpen(true);
  };

  const save = async () => {
    try {
      const v = await form.validateFields();
      if (editing) await hrmsService.updateGrade(editing.id, v);
      else await hrmsService.createGrade(v);
      message.success(editing ? 'Updated' : 'Created');
      setDrawerOpen(false);
      queryClient.invalidateQueries({ queryKey: hrmsKeys.grades() });
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Save failed');
    }
  };

  const remove = (r: any) => {
    modal.confirm({
      title: `Delete grade ${r.grade_code}?`,
      content: 'Soft-deletes the grade. Existing employees keep their grade text.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await hrmsService.deleteGrade(r.id);
          message.success('Deleted');
          queryClient.invalidateQueries({ queryKey: hrmsKeys.grades() });
        } catch (e: any) {
          message.error(e?.message || 'Delete failed');
        }
      },
    });
  };

  const columns: ColumnsType<any> = [
    { title: 'Code', dataIndex: 'grade_code', width: 110 },
    { title: 'Name', dataIndex: 'grade_name' },
    { title: 'Rank', dataIndex: 'level_rank', width: 80 },
    {
      title: 'Description',
      dataIndex: 'description',
      ellipsis: true,
      render: (v: string) => v || '—',
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      width: 80,
      render: (v: boolean) => <Tag color={v !== false ? 'green' : 'default'}>{v !== false ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Actions',
      key: 'a',
      width: 100,
      render: (_, r) => (
        <Space>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => remove(r)} />
        </Space>
      ),
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Salary grades"
          subtitle="Grade band master used on salary revisions and employee profiles"
          primaryActions={
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add grade
            </Button>
          }
        />
      }
    >
      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 20 }}
        locale={{
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No grades yet" />,
        }}
      />

      <Drawer
        title={editing ? 'Edit grade' : 'New grade'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={420}
        destroyOnClose
        extra={
          <Button type="primary" onClick={save}>
            Save
          </Button>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="grade_code" label="Code" rules={[{ required: true }]}>
            <Input disabled={Boolean(editing)} placeholder="G5" />
          </Form.Item>
          <Form.Item name="grade_name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="Grade 5" />
          </Form.Item>
          <Form.Item name="level_rank" label="Level rank" rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </WorkspaceLayout>
  );
};

export default GradesPage;
